import { BreakoutIcon } from '@daily/shared/components/Icons/BreakoutIcon';
import { MicrophoneIcon } from '@daily/shared/components/Icons/MicrophoneIcon';
import { RecordIcon } from '@daily/shared/components/Icons/RecordIcon';
import { useSnackbar } from '@daily/shared/contexts/Snackbar';
import {
  DailyEventObjectMeetingSessionStateUpdated,
  DailyEventObjectParticipant,
  DailyEventObjectParticipantLeft,
} from '@daily-co/daily-js';
import {
  ExtendedDailyParticipant,
  useDaily,
  useDailyEvent,
  useLocalSessionId,
  useParticipant,
  useParticipantIds,
  useRecording,
  useScreenShare,
  useThrottledDailyEvent,
} from '@daily-co/daily-react';
import deepEqual from 'fast-deep-equal';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { atom, useRecoilCallback, useRecoilValue } from 'recoil';

import { MAIN_ROOM_IDENTIFIER } from '../components/BreakoutRooms/helpers/constants';
import { useBreakoutSessionState } from '../components/BreakoutRooms/hooks/useBreakoutSessionState';
import { useCallConfig } from '../hooks/useCallConfig';
import { useForceLowerHand, useHasHandRaised } from '../hooks/useHandRaising';
import { usePreviousValue } from '../hooks/usePreviousValue';
import { isParticipantTrackOff } from '../lib/participants';
import { useBreakoutView, useJoinBreakoutModal } from '../lib/state/breakouts';
import { usePinnedId } from '../lib/state/layout';
import { getPrebuiltUserData, setPrebuiltUserData } from '../lib/userData';
import { useCallState } from './CallProvider';

const MEETING_DATA_BREAKOUTS_KEY = 'br';

export interface BreakoutRoom {
  id: string;
  name: string;
  users: string[];
}

export interface BreakoutSession {
  autojoin?: boolean;
  endDate?: string;
}

interface BreakoutsContextValue {
  /**
   * Aborts end of breakouts.
   */
  abortEndingBreakouts(): void;
  /**
   * Assigns the breakout host role to the participant identified by the given id.
   * @param idOfHost The session_id for the new breakouts host.
   */
  setBreakoutHost(idOfHost?: string): void;
  /**
   * Ends ongoing breakouts, either immediately or with a 60s delay.
   * @param endNow Ends breakouts immediately, when enabled.
   */
  endBreakouts(endNow?: boolean): Promise<void>;
  /**
   * Joins the currently assigned breakout room.
   */
  joinBreakout(): void;
  /**
   * Leaves the breakout room and switches back to the main room.
   * @param clearConfig When set, the breakout configuration from userData.
   */
  leaveBreakout(clearConfig?: boolean): void;
  /**
   * Declines the invitation to join a breakout session.
   * @param clearConfig When set, the breakout configuration from userData.
   */
  declineBreakout(clearConfig?: boolean): void;
  /**
   * Moves participants to a breakout room.
   * @param movements An object where keys are participant session_ids, values are breakout ids.
   */
  moveParticipants(movements: Record<string, string>): void;
  /**
   * Sets up breakout rooms and starts breakout sessions.
   * @param mapping Contains breakout rooms configuration. Keys are unique identifiers for each room.
   * @param sessionConfig The breakout session configuration.
   */
  setupBreakouts(
    mapping: Record<string, BreakoutRoom>,
    sessionConfig: BreakoutSession
  ): Promise<void>;
}

const noOpWarn = () => {
  console.warn('This call is not configured to use breakout rooms.');
};

const noOpWarnPromise = () =>
  new Promise<void>((resolve) => {
    noOpWarn();
    resolve();
  });

const BreakoutsContext = createContext<BreakoutsContextValue>({
  abortEndingBreakouts: noOpWarn,
  setBreakoutHost: noOpWarn,
  endBreakouts: noOpWarnPromise,
  joinBreakout: noOpWarn,
  declineBreakout: noOpWarn,
  leaveBreakout: noOpWarn,
  moveParticipants: noOpWarn,
  setupBreakouts: noOpWarnPromise,
});

type UserStatus = 'invited' | 'joined' | 'idle' | 'declined' | 'left';
export interface BreakoutsUserData {
  br?: {
    currentRoomId?: string;
    help?: boolean;
    status?: UserStatus;
  };
}

export interface BreakoutsSessionData {
  [MEETING_DATA_BREAKOUTS_KEY]?: {
    /**
     * Contains a map of the rooms with the room’s names and their assigned user ids
     */
    mapping?: {
      [id: string]: {
        name: string;
        users: string[];
      };
    };
    /**
     * Contains the session id of the breakout host
     */
    host?: string;
    /**
     * Contains the status of the breakout session
     */
    status?: 'ongoing' | 'ended';
    /**
     * Contains breakout configuration
     */
    config?: BreakoutSession;
  };
}

const breakoutJoiningState = atom<boolean>({
  key: 'breakout-joining',
  default: false,
  effects: [
    ({ onSet, resetSelf }) => {
      onSet((newVal) => {
        if (newVal) {
          setTimeout(resetSelf, 2000);
        }
      });
    },
  ],
});

export const useRenderBreakoutsInterstitialScreen = () =>
  useRecoilValue(breakoutJoiningState);

export const BreakoutsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const { t } = useTranslation();
  const daily = useDaily();
  const { enableBreakoutRooms } = useCallConfig();
  const localSessionId = useLocalSessionId();
  const [, setJoinBreakoutModal] = useJoinBreakoutModal();
  const { addMessage } = useSnackbar();
  const { state } = useCallState();

  const hasHandRaised = useHasHandRaised(localSessionId);
  const { forceLowerHand } = useForceLowerHand();
  const { isSharingScreen, stopScreenShare } = useScreenShare();
  const [, setPinnedId] = usePinnedId();

  /**
   * Cleans up the local participant's state:
   * - Hand is lowered
   * - Mic is muted
   * - Screen share is stopped
   * - Pinned id is reset
   */
  const handleRoomSwitch = useCallback(() => {
    // When a user joins a session, the context changes, so reset hand raise.
    hasHandRaised && forceLowerHand(localSessionId, true);

    // When the context for a user changes (e.g. different breakout room, or the session ends), we'll mute them
    const isMicOn = !isParticipantTrackOff(
      daily?.participants()?.local,
      'audio'
    );

    if (isMicOn) {
      daily.setLocalAudio(false);
      addMessage({
        content: t('notification.micMutedAutomatically'),
        type: 'info',
        icon: <MicrophoneIcon muted size={16} />,
      });
    }

    // User is transitioning between rooms, so stop screen sharing.
    isSharingScreen && stopScreenShare();

    // Reset pinned participant
    setPinnedId('');
  }, [
    addMessage,
    daily,
    forceLowerHand,
    hasHandRaised,
    isSharingScreen,
    localSessionId,
    setPinnedId,
    stopScreenShare,
    t,
  ]);

  const {
    breakoutHost,
    breakoutParticipantIds,
    breakoutStatus,
    invitedBreakoutRoom,
  } = useBreakoutSessionState({
    onMeetingSessionStateUpdated: useRecoilCallback(
      ({ set }) =>
        async (ev: DailyEventObjectMeetingSessionStateUpdated) => {
          const userData = getPrebuiltUserData(daily) as BreakoutsUserData;
          const meetingData = ev.meetingSessionState
            .data as BreakoutsSessionData;
          const breakouts = meetingData?.[MEETING_DATA_BREAKOUTS_KEY];

          const assigned = Object.entries(breakouts?.mapping ?? {}).find(
            ([, room]) => room.users.includes(localSessionId)
          );

          if (breakouts?.status === 'ended') {
            // Breakout session ended by host or countdown
            if (typeof userData === 'object' && 'br' in userData) {
              handleRoomSwitch();
              delete userData.br;
            }
            setPrebuiltUserData(daily, { ...userData });
            setJoinBreakoutModal(false);
          } else if (assigned?.[0] !== userData?.br?.currentRoomId) {
            // Breakout session started and local participant is assigned to room
            const autojoin =
              Boolean(breakouts?.config?.autojoin) ||
              breakouts?.host === localSessionId;
            const newUserData: BreakoutsUserData = {
              ...userData,
              br: {
                ...(userData?.br ?? {}),
                currentRoomId: autojoin
                  ? assigned?.[0]
                  : userData?.br?.currentRoomId,
                status: autojoin ? 'joined' : 'invited',
              },
            };
            await setPrebuiltUserData<BreakoutsUserData>(daily, newUserData);
            if (!autojoin) {
              setJoinBreakoutModal(true);
            } else {
              handleRoomSwitch();
              set(breakoutJoiningState, true);
            }
          }
        },
      [daily, handleRoomSwitch, localSessionId, setJoinBreakoutModal]
    ),
  });

  const setBreakoutHost = useCallback(
    (idOfHost = localSessionId) => {
      const data = daily.meetingSessionState().data as BreakoutsSessionData;
      daily.setMeetingSessionData(
        {
          br: {
            ...data[MEETING_DATA_BREAKOUTS_KEY],
            host: idOfHost,
          },
        },
        'shallow-merge'
      );
    },
    [daily, localSessionId]
  );

  const host = useParticipant(breakoutHost);
  const otherOwnerIds = useParticipantIds({
    filter: useCallback(
      (p: ExtendedDailyParticipant) => {
        return p.owner && p.session_id !== breakoutHost;
      },
      [breakoutHost]
    ),
    sort: 'joined_at',
  });
  /**
   * In case breakout host leaves the call, auto-assign breakout host to first remaining host.
   */
  useDailyEvent(
    'participant-left',
    useCallback(
      (ev: DailyEventObjectParticipantLeft) => {
        if (
          !breakoutHost ||
          ev.participant.session_id !== breakoutHost ||
          otherOwnerIds?.[0] !== localSessionId
        )
          return;
        if (
          ev.participant.session_id === breakoutHost &&
          otherOwnerIds?.[0] === localSessionId
        ) {
          setBreakoutHost();
        }
      },
      [breakoutHost, localSessionId, otherOwnerIds, setBreakoutHost]
    )
  );
  /**
   * When joining as a host and last breakout host doesn't exist,
   * assign breakout host role.
   */
  useEffect(
    function assignNewBreakoutHost() {
      if (state !== 'joined' || breakoutStatus !== 'ongoing') return;
      const timeout = setTimeout(() => {
        if (host) return;
        if (otherOwnerIds?.[0] === localSessionId) {
          setBreakoutHost();
        }
      }, 3000);
      return () => {
        clearTimeout(timeout);
      };
    },
    [
      breakoutStatus,
      host,
      localSessionId,
      otherOwnerIds,
      setBreakoutHost,
      state,
    ]
  );

  /**
   * When local participant became new breakout host,
   * display a snackbar message.
   */
  const prevBreakoutHost = usePreviousValue(breakoutHost);
  useEffect(
    function notifyNewBreakoutHost() {
      if (
        prevBreakoutHost &&
        prevBreakoutHost !== localSessionId &&
        breakoutHost === localSessionId
      ) {
        addMessage({
          content: t('breakouts.newHostMessage'),
          icon: <BreakoutIcon size={16} />,
          type: 'info',
        });
      }
    },
    [addMessage, breakoutHost, localSessionId, prevBreakoutHost, t]
  );

  const joinBreakout: BreakoutsContextValue['joinBreakout'] = useRecoilCallback(
    ({ set }) =>
      () => {
        const prevUserData = getPrebuiltUserData(daily) as BreakoutsUserData;

        if (prevUserData?.br?.status === 'invited') {
          set(breakoutJoiningState, true);
        }

        handleRoomSwitch();

        setPrebuiltUserData<BreakoutsUserData>(daily, {
          ...prevUserData,
          br: {
            currentRoomId: invitedBreakoutRoom?.id,
            status: 'joined',
          },
        });
      },
    [daily, handleRoomSwitch, invitedBreakoutRoom?.id]
  );

  const leaveBreakout: BreakoutsContextValue['leaveBreakout'] =
    useCallback(() => {
      const prevUserData = getPrebuiltUserData(daily);

      handleRoomSwitch();

      setPrebuiltUserData<BreakoutsUserData>(daily, {
        ...prevUserData,
        br: {
          status: 'left',
        },
      });
    }, [daily, handleRoomSwitch]);

  const { isRecording, stopRecording } = useRecording();

  const setupBreakouts: BreakoutsContextValue['setupBreakouts'] = useCallback(
    async (roomMapping, sessionConfig) => {
      await daily?.setNetworkTopology({ topology: 'sfu' });
      const newSessionData: BreakoutsSessionData = {
        [MEETING_DATA_BREAKOUTS_KEY]: {
          mapping: roomMapping,
          host: localSessionId,
          status: 'ongoing',
          config: sessionConfig,
        },
      };

      if (isRecording) {
        stopRecording();
        addMessage({
          content: t('recording.status.stopped'),
          type: 'info',
          icon: <RecordIcon size={16} />,
        });
      }

      return new Promise((resolve) => {
        const handleSessionStateUpdate = (
          ev: DailyEventObjectMeetingSessionStateUpdated
        ) => {
          const data = ev.meetingSessionState.data;
          if (
            deepEqual(
              data[MEETING_DATA_BREAKOUTS_KEY],
              newSessionData[MEETING_DATA_BREAKOUTS_KEY]
            )
          ) {
            resolve();
          }
        };
        daily.on('meeting-session-state-updated', handleSessionStateUpdate);
        daily.setMeetingSessionData(newSessionData, 'shallow-merge');
      });
    },
    [addMessage, daily, isRecording, localSessionId, stopRecording, t]
  );

  const endTimeout = useRef<ReturnType<typeof setTimeout>>(null);
  const [, setBreakoutView] = useBreakoutView();

  const endBreakouts: BreakoutsContextValue['endBreakouts'] = useCallback(
    async (endNow = false) => {
      const sessionData = daily.meetingSessionState()
        .data as BreakoutsSessionData;

      if (endNow) {
        const newSessionData: BreakoutsSessionData = {
          [MEETING_DATA_BREAKOUTS_KEY]: {
            ...sessionData[MEETING_DATA_BREAKOUTS_KEY],
            status: 'ended',
            host: '',
            config: {
              autojoin:
                sessionData[MEETING_DATA_BREAKOUTS_KEY]?.config?.autojoin,
            },
          },
        };

        clearTimeout(endTimeout.current);

        return new Promise<void>((resolve) => {
          const handleSessionStateUpdate = (
            ev: DailyEventObjectMeetingSessionStateUpdated
          ) => {
            if (
              deepEqual(
                ev.meetingSessionState.data[MEETING_DATA_BREAKOUTS_KEY],
                newSessionData[MEETING_DATA_BREAKOUTS_KEY]
              )
            ) {
              daily.off(
                'meeting-session-state-updated',
                handleSessionStateUpdate
              );
              setBreakoutView('start');
              resolve();
            }
          };
          daily.on('meeting-session-state-updated', handleSessionStateUpdate);
          daily.setMeetingSessionData(newSessionData, 'replace');
        });
      }

      const endDate = new Date(Date.now() + 60000);
      const newSessionData: BreakoutsSessionData = {
        [MEETING_DATA_BREAKOUTS_KEY]: {
          ...sessionData[MEETING_DATA_BREAKOUTS_KEY],
          config: {
            ...(sessionData[MEETING_DATA_BREAKOUTS_KEY]?.config ?? {}),
            endDate: endDate.toISOString(),
          },
        },
      };

      daily.setMeetingSessionData(newSessionData, 'shallow-merge');

      clearTimeout(endTimeout.current);
      endTimeout.current = setTimeout(() => {
        endBreakouts(true);
      }, 60000);
    },
    [daily, setBreakoutView]
  );

  const abortEndingBreakouts: BreakoutsContextValue['abortEndingBreakouts'] =
    useCallback(() => {
      const sessionData = daily.meetingSessionState()
        .data as BreakoutsSessionData;

      const newSessionData: BreakoutsSessionData = {
        [MEETING_DATA_BREAKOUTS_KEY]: {
          ...sessionData[MEETING_DATA_BREAKOUTS_KEY],
          config: {
            ...sessionData[MEETING_DATA_BREAKOUTS_KEY].config,
            endDate: null,
          },
        },
      };

      daily.setMeetingSessionData(newSessionData, 'shallow-merge');
      clearTimeout(endTimeout.current);
    }, [daily]);

  const moveParticipants: BreakoutsContextValue['moveParticipants'] =
    useCallback(
      (movements) => {
        const sessionData = daily.meetingSessionState()
          .data as BreakoutsSessionData;
        const mapping = {
          ...sessionData[MEETING_DATA_BREAKOUTS_KEY].mapping,
        };

        const findUser = (sessionId: string) => {
          for (const [roomId, config] of Object.entries(mapping)) {
            if (config.users.includes(sessionId)) return roomId;
          }
          return null;
        };

        for (const [sessionId, roomId] of Object.entries(movements)) {
          const currentRoom = findUser(sessionId);

          // First, edit the session data: REMOVE the user from whatever current room they are in.
          if (currentRoom) {
            mapping[currentRoom] = {
              name: mapping[currentRoom].name,
              users: [
                ...mapping[currentRoom].users.filter((id) => id !== sessionId),
              ],
            };
          }

          // then add the user to their new room (unless it's the main room)
          if (roomId && roomId !== MAIN_ROOM_IDENTIFIER) {
            mapping[roomId] = {
              name: mapping[roomId].name,
              users: [...mapping[roomId].users, sessionId],
            };
          }

          // set the new data
          daily.setMeetingSessionData(
            {
              br: {
                ...sessionData.br,
                mapping,
              },
            },
            'shallow-merge'
          );
        }
      },
      [daily]
    );

  const declineBreakout: BreakoutsContextValue['declineBreakout'] =
    useCallback(() => {
      const prevUserData = getPrebuiltUserData(daily) as BreakoutsUserData;
      setPrebuiltUserData<BreakoutsUserData>(daily, {
        ...prevUserData,
        br: {
          status: 'declined',
          currentRoomId: prevUserData.br?.currentRoomId,
        },
      });
    }, [daily]);

  useThrottledDailyEvent(
    'participant-updated',
    useCallback(
      (events: DailyEventObjectParticipant[]) => {
        if (breakoutHost !== localSessionId) return;
        const movements = {};
        events.forEach((event) => {
          const userData = getPrebuiltUserData(
            daily,
            event.participant.local ? 'local' : event.participant.session_id
          ) as BreakoutsUserData;
          if (
            userData?.br?.status === 'declined' ||
            userData?.br?.status === 'left'
          ) {
            movements[event.participant.session_id] =
              userData?.br?.currentRoomId ?? MAIN_ROOM_IDENTIFIER;
          }
        });
        if (Object.keys(movements).length === 0) return;
        moveParticipants(movements);
      },
      [breakoutHost, daily, localSessionId, moveParticipants]
    )
  );

  useThrottledDailyEvent(
    'participant-left',
    useCallback(
      (evts: DailyEventObjectParticipantLeft[]) => {
        if (breakoutHost !== localSessionId) return;
        const movements = {};
        evts.forEach((ev) => {
          // moving left participants to the main room, removes them from the list of breakout participants
          movements[ev.participant.session_id] = MAIN_ROOM_IDENTIFIER;
        });
        if (Object.keys(movements).length === 0) return;
        moveParticipants(movements);
      },
      [breakoutHost, localSessionId, moveParticipants]
    )
  );

  /**
   * Cancel request for help, when breakout host joined this room.
   */
  useEffect(() => {
    if (breakoutParticipantIds.includes(breakoutHost)) {
      const prevUserData: BreakoutsUserData = getPrebuiltUserData(daily);
      // Ignore if help is already falsy to prevent unnecessary writes to userData
      if (!prevUserData?.br?.help) return;
      setPrebuiltUserData(daily, {
        ...(prevUserData ?? {}),
        br: {
          ...(prevUserData?.br ?? {}),
          help: false,
        },
      });
    }
  }, [breakoutHost, breakoutParticipantIds, daily]);

  if (!enableBreakoutRooms) {
    return <>{children}</>;
  }

  // TODO: Remove me, when you're done with debugging!
  window['breakouts'] = {
    abortEndingBreakouts,
    endBreakouts,
    joinBreakout,
    leaveBreakout,
    declineBreakout,
    moveParticipants,
    setupBreakouts,
  };

  return (
    <BreakoutsContext.Provider
      value={{
        abortEndingBreakouts,
        setBreakoutHost,
        endBreakouts,
        declineBreakout,
        joinBreakout,
        leaveBreakout,
        moveParticipants,
        setupBreakouts,
      }}
    >
      {children}
    </BreakoutsContext.Provider>
  );
};

export const useBreakouts = () => useContext(BreakoutsContext);
